import { useState } from 'react'

import { Box, Button, Callout } from '@factorialco/deprecated-design-system'
import {
  CompetencyLevelFragment,
  CompetencyLevelsDataCollectionDocument,
  CompetencyLevelsReorderModalDocument,
  CompetencyLevelsReorderModalLevelsDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ReorderLevelsComponent } from './ReorderLevelsComponent'

export interface FormCompetencyLevel {
  id: number
  name: string
  position: number
}

interface ContentProps {
  competencyId: number
  onClose: () => void
  onSuccess: () => void
  allCompetencyLevels: CompetencyLevelFragment[] | undefined
  loadingAllCompetencyLevels: boolean
}

const CompetencyLevelsReorderModalContent = ({
  competencyId,
  onClose,
  onSuccess,
  allCompetencyLevels,
  loadingAllCompetencyLevels,
}: ContentProps) => {
  const [levels, setLevels] = useState<FormCompetencyLevel[]>([])

  useUnsafeEffect(() => {
    if (allCompetencyLevels) {
      setLevels(
        [...allCompetencyLevels]
          .sort((a, b) => a.position - b.position)
          .map((v, index) => ({
            id: v.id,
            name: v.name,
            position: index + 1,
          }))
      )
    }
  }, [allCompetencyLevels])

  const [reorder, { loading: loadingReorder }] = useMutation(CompetencyLevelsReorderModalDocument, {
    extractorFn: (data) =>
      data.competenciesMutations.reorderByCompetencyCompetenciesCompetencyLevel,
  })

  const handleCompetencyLevelsReorder = (updatedContent: FormCompetencyLevel[]) => {
    const updatedContentWithNewPositions = updatedContent.map((v, index) => ({
      id: v.id,
      name: v.name,
      position: index + 1,
    }))
    setLevels(updatedContentWithNewPositions)
  }

  const handleSubmit = async () => {
    const result = await reorder({
      variables: {
        competencyId: competencyId,
        levelPositions: levels.map((v) => ({
          id: v.id,
          position: v.position,
        })),
      },
      refetchQueries: [CompetencyLevelsDataCollectionDocument],
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onSuccess()
    }
    return handled
  }

  return (
    <Modal
      title={i18n.t('competencies_v3.levels.reorder.title')}
      illustration="top-up"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit loading={loadingReorder}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s32">
        {loadingAllCompetencyLevels && <Loading />}
        {allCompetencyLevels && !loadingAllCompetencyLevels && (
          <ReorderLevelsComponent levels={levels} onReorder={handleCompetencyLevelsReorder} />
        )}

        <Callout
          type="warning"
          description={i18n.t('competencies_v3.levels.reorder.warning')}
          hideIcon
        />
      </Box>
    </Modal>
  )
}

interface Props {
  competencyId: number
  onClose: () => void
  onSuccess: () => void
}

export const CompetencyLevelsReorderModal = ({ competencyId, onClose, onSuccess }: Props) => {
  const { data: allCompetencyLevels, loading: loadingAllCompetencyLevels } = useQuery(
    CompetencyLevelsReorderModalLevelsDocument,
    {
      variables: {
        competencyIds: [competencyId],
      },
    }
  )

  return (
    <CompetencyLevelsReorderModalContent
      competencyId={competencyId}
      onClose={onClose}
      onSuccess={onSuccess}
      allCompetencyLevels={allCompetencyLevels?.competencies.competencyLevels}
      loadingAllCompetencyLevels={loadingAllCompetencyLevels}
    />
  )
}
